import { type ReactElement, useCallback } from 'react'
import { useSelector } from 'react-redux'

import { Stack } from '@mui/material'
import { format } from 'date-fns/esm'

import { selectUserConfiguration } from '@/features/domain/user'

import { CalendarPicker } from './components/CalendarPicker'
import { EligibilityTypePicker } from './components/EligibilityTypePicker'

type Props = {
  value: uui.domain.client.rm.Eligibility
  onChange: (eligibility: uui.domain.client.rm.Eligibility) => void
  messages?: ReactElement
  label: string
  testId?: string
}

export function EligibilityPicker(props: Props) {
  const { value, onChange, messages, label, testId } = props

  const { today } = useSelector(selectUserConfiguration)

  const handleOnChangeType = useCallback(
    (type: uui.domain.client.rm.Eligibility['type']) => {
      switch (type) {
        case 'any':
          onChange({
            type: 'any',
          })
          return

        case 'on': {
          onChange({
            type: 'on',
            dates: [today],
          })
          return
        }

        case 'by': {
          onChange({
            type: 'by',
            date: today,
          })
          return
        }
      }
    },
    [onChange, today],
  )

  const handleOnChangeDate = useCallback(
    (type: uui.domain.client.rm.Eligibility['type'], dates: Date[]) => {
      switch (type) {
        case 'any':
          onChange({
            type: 'any',
          })
          return

        case 'on': {
          const datesAsString = dates.map(d => format(d, 'yyyyMMdd'))
          onChange({
            type: 'on',
            dates: datesAsString,
          })
          return
        }

        case 'by': {
          const date = format(dates[0], 'yyyyMMdd')
          onChange({
            type: 'by',
            date,
          })
          return
        }
      }
    },
    [onChange],
  )

  return (
    <Stack direction="column" height="100%" width="100%" data-testId={testId} spacing={4}>
      <EligibilityTypePicker type={value.type} onChangeType={handleOnChangeType} label={label} />
      <CalendarPicker eligibility={value} onChange={handleOnChangeDate} />
      {!!messages && messages}
    </Stack>
  )
}
